/**
 * DER BEREICH NE4 (.333, Lieferung L1): Übersicht, Firmen und Teams.
 *
 * ORIGINALTEXT MARVIN (08.10.2026)
 *   „Bei deren Beauftragung handelt es sich immer um ein Team mit zwei
 *    Personen. Und es gibt einen Teamzugang, keinen Personenzugang."
 *   „Es gibt für den Bereich einen Nachunternehmer, der seine Mitarbeiter
 *    stellt. … Die darf er natürlich nicht sehen. Und auch nicht deren Termine
 *    oder sowas. Das darf nur dann der Innendienst bzw. die Dispo sehen."
 *
 * Was ein Firmenzugang sieht, entscheidet der Server (`routen/ne4.ts`): hier
 * kommen seine Firma und seine Teams an, und sonst nichts. Die Oberfläche
 * zeigt, was kommt.
 */
import { useEffect, useState } from 'react';
import { api, darf, type Anmelder } from '../api.ts';
import { Kopf, Karte } from '../teile/Geruest.tsx';
import { Dialog, Fehlerkasten, Text, Auswahl, Haken, ausFehler, type Fehlerlage } from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { Mailtexte, Laeufe } from './Ne4Planung.tsx';
import { Links } from './Stammdaten.tsx';
import { eingeben } from '../teile/rueckfrage.tsx';

export function Ne4Teams({ ich, eingebettet }: { ich: Anmelder; eingebettet?: boolean }) {
  const [firmen, setFirmen] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [benutzer, setBenutzer] = useState<any[]>([]);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [dialog, setDialog] = useState<{ art: 'firma' | 'team' | 'zugaenge' | 'ne3'; zeile?: any } | null>(null);
  const pflegt = darf(ich, 'ne4.teams.pflegen');

  const laden = async () => {
    try {
      const [f, t] = await Promise.all([api.ne4Firmen(), api.ne4Teams()]);
      setFirmen(f); setTeams(t);
      if (pflegt) {
        setBenutzer(await api.ne4Benutzer());
      }
    } catch (f) { setFehler(ausFehler(f)); }
  };
  useEffect(() => { laden(); }, []);
  useFrisch(laden);

  return (
    <>
      {eingebettet ? (pflegt && (
        <div className="reihe" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
          <button type="button" className="knopf" onClick={() => setDialog({ art: 'ne3' })}>Aus NE3 übernehmen</button>
          <button type="button" className="knopf" onClick={() => setDialog({ art: 'firma' })}>Neue NE4-Firma</button>
          <button type="button" className="knopf haupt" disabled={!firmen.length} onClick={() => setDialog({ art: 'team' })}>Neues Team</button>
        </div>
      )) : <Kopf titel="NE4, Firmen und Teams" weg={`${firmen.length} Firmen, ${teams.length} Teams`}
            rechts={pflegt ? (
              <div className="reihe">
                <button type="button" className="knopf" onClick={() => setDialog({ art: 'ne3' })}>Aus NE3 übernehmen</button>
                <button type="button" className="knopf" onClick={() => setDialog({ art: 'firma' })}>Neue Firma</button>
                <button type="button" className="knopf haupt" disabled={!firmen.length} onClick={() => setDialog({ art: 'team' })}>Neues Team</button>
              </div>
            ) : undefined} />}
      <div className={eingebettet ? undefined : 'inhalt'}>
      <Fehlerkasten lage={fehler} />
      <Karte titel="Firmen">
        <p className="klein leise" style={{ marginTop: 0 }}>
          Ein Firmenzugang sieht nur die gebuchten Termine und Zahlen der eigenen Teams. Den Eingang, die Anrufliste und die anderen Firmen sieht nur die Dispo.
          Jede Firma hat ihr erstes Team von selbst; arbeitet eine Firma nur über ihre Disponentin, reicht dieses Team und ein Firmenzugang.
        </p>
        {!firmen.length && <div className="leise klein">Noch keine Firma. Die erste ist meist MDS selbst (eigene Teams).</div>}
        {!!firmen.length && (
          <table className="liste">
            <thead><tr><th>Firma</th><th>Kürzel</th><th className="zahl">Teams</th><th>Firmenzugänge</th><th /></tr></thead>
            <tbody>
              {firmen.map((f) => (
                <tr key={f.id} style={{ cursor: 'default', opacity: f.aktiv ? 1 : 0.55 }}>
                  <td><b>{f.name}</b>{f.eigene && <span className="klein leise">, eigene Teams</span>}{f.subunternehmer_id && <span className="klein leise">, aus NE3</span>}{!f.aktiv && <span className="klein leise">, stillgelegt</span>}
                    {f.notiz && <div className="klein leise">{f.notiz}</div>}</td>
                  <td><code>{f.kurz}</code></td>
                  <td className="zahl">{f.teams}</td>
                  <td className="klein">{(f.zugaenge ?? []).map((z: any) => z.name).join(', ') || <span className="leise">keiner</span>}</td>
                  <td className="knopfleiste breit">
                    {pflegt && <button type="button" className="knopf klein" onClick={() => setDialog({ art: 'firma', zeile: f })}>ändern</button>}
                    {pflegt && <button type="button" className="knopf klein" onClick={() => setDialog({ art: 'zugaenge', zeile: f })}>Zugänge</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Karte>

      <Karte titel="Teams">
        <p className="klein leise" style={{ marginTop: 0 }}>
          Ein Team sind 2 Personen mit einem Teamzugang. Der Zugang ist ein Benutzer in der Gruppe „NE4 Teamzugang"; er sieht seinen Tagesplan und meldet fertig.
        </p>
        {!teams.length && <div className="leise klein">Noch kein Team.</div>}
        {!!teams.length && (
          <table className="liste">
            <thead><tr><th>Team</th><th>Firma</th><th>Teamzugang</th><th>Darf</th><th>Start</th><th /></tr></thead>
            <tbody>
              {teams.map((t) => (
                <tr key={t.id} style={{ cursor: 'default', opacity: t.aktiv ? 1 : 0.55 }}>
                  <td><b>{t.name}</b>{t.personen && <div className="klein leise">{t.personen}</div>}{!t.aktiv && <span className="klein leise">, stillgelegt</span>}</td>
                  <td>{t.firma} <span className="klein leise">({t.firma_kurz})</span></td>
                  <td className="klein">{t.zugang_name ? <>{t.zugang_name}<div className="leise">{t.zugang_email}</div></> : <span className="hinweis warnung" style={{ display: 'inline-block', padding: '2px 8px' }}>ohne Zugang</span>}</td>
                  <td className="klein">{[t.darf_hc && 'HC', t.darf_gwv && 'GWV'].filter(Boolean).join(', ') || <span className="leise">nichts</span>}</td>
                  <td className="klein">{[t.start_plz, t.start_ort].filter(Boolean).join(' ')}</td>
                  <td className="knopfleiste breit">
                    {pflegt && <button type="button" className="knopf klein" onClick={() => setDialog({ art: 'team', zeile: t })}>ändern</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Karte>

      {dialog?.art === 'firma' && <FirmaDialog zeile={dialog.zeile} zu={() => setDialog(null)} fertig={() => { setDialog(null); laden(); }} />}
      {dialog?.art === 'team' && <TeamDialog zeile={dialog.zeile} firmen={firmen} benutzer={benutzer} zu={() => setDialog(null)} fertig={() => { setDialog(null); laden(); }} />}
      {dialog?.art === 'ne3' && <Ne3UebernahmeDialog zu={() => setDialog(null)} fertig={() => { setDialog(null); laden(); }} />}
      {dialog?.art === 'zugaenge' && <ZugaengeDialog firma={dialog.zeile} benutzer={benutzer} zu={() => setDialog(null)} fertig={() => { setDialog(null); laden(); }} />}
      </div>
    </>
  );
}

function FirmaDialog({ zeile, zu, fertig }: { zeile?: any; zu: () => void; fertig: () => void }) {
  const [name, setName] = useState(zeile?.name ?? '');
  const [kurz, setKurz] = useState(zeile?.kurz ?? '');
  const [eigene, setEigene] = useState<boolean>(!!zeile?.eigene);
  const [email, setEmail] = useState(zeile?.email ?? '');
  const [telefon, setTelefon] = useState(zeile?.telefon ?? '');
  const [notiz, setNotiz] = useState(zeile?.notiz ?? '');
  const [aktiv, setAktiv] = useState<boolean>(zeile ? !!zeile.aktiv : true);
  /* .583: STARTORT FÜR DIE GANZE FIRMA. Marvin, 02.11.2026: „Start,
     Postleitzahl und Ort könnte man auch dann einmal generell für die Firma
     eintragen, was dann das, was die einzelnen Teams haben, überschreibt ...
     ich gebe die Postleitzahl an und er findet den Ort automatisch." */
  const [plz, setPlz] = useState(zeile?.start_plz ?? '');
  const [ort, setOrt] = useState(zeile?.start_ort ?? '');
  const [fuerTeams, setFuerTeams] = useState(true);
  async function plzGesetzt(w: string) {
    setPlz(w);
    if (/^\d{5}$/.test(w.trim())) {
      try { const o = await api.ne4OrtZurPlz(w.trim()); if (o.ort) setOrt(o.ort); } catch { /* ohne Vorschlag */ }
    }
  }
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  async function speichern() {
    setLaeuft(true); setFehler(null);
    try {
      const k = { name, kurz, eigene, email: email || null, telefon: telefon || null, notiz: notiz || null, aktiv,
                  start_plz: plz.trim() || null, start_ort: ort.trim() || null, teams_uebernehmen: fuerTeams };
      if (zeile) await api.ne4FirmaAendern(zeile.id, k); else await api.ne4FirmaAnlegen(k);
      fertig();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  return (
    <Dialog titel={zeile ? 'Firma ändern' : 'Neue Firma'} breite={560} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      <Text name="name" titel="Name" wert={name} setze={setName} pflicht fehler={fehler} />
      <Text name="kurz" titel="Kürzel" wert={kurz} setze={setKurz} pflicht fehler={fehler} hinweis="1 bis 12 Zeichen, Sonderzeichen erlaubt, zum Beispiel MDS oder A&A" breite={200} />
      <Haken name="eigene" titel="Eigene Teams (MDS selbst)" wert={eigene} setze={setEigene} />
      <Text name="email" titel="E-Mail" art="email" wert={email} setze={setEmail} fehler={fehler} />
      <Text name="telefon" titel="Telefon" art="tel" wert={telefon} setze={setTelefon} fehler={fehler} />
      <div className="ne4-startzeile">
        <Text name="start_plz" titel="Start-PLZ" wert={plz} setze={(w) => void plzGesetzt(w)} fehler={fehler} breite={120} />
        <Text name="start_ort" titel="Startort" wert={ort} setze={setOrt} fehler={fehler} breite={260} />
      </div>
      {zeile && <Haken name="teams_uebernehmen" titel="Startort für alle Teams der Firma übernehmen" wert={fuerTeams} setze={setFuerTeams}
        hinweis="Überschreibt den Startort, der bei den einzelnen Teams steht. Neue Teams bekommen ihn ohnehin." />}
      <Text name="notiz" titel="Notiz" wert={notiz} setze={setNotiz} mehrzeilig fehler={fehler} />
      {zeile && <Haken name="aktiv" titel="Aktiv" wert={aktiv} setze={setAktiv} hinweis="Stillgelegt heißt: keine neuen Teamtage, die Zahlen bleiben." />}
      <div className="reihe" style={{ marginTop: 12 }}>
        <button type="button" className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
        <button type="button" className="knopf haupt" onClick={speichern} disabled={laeuft || !name.trim() || !kurz.trim()}>Speichern</button>
      </div>
    </Dialog>
  );
}

/* .585: NE3-FIRMA FÜR NE4 ÜBERNEHMEN. Marvin, 02.11.2026: „Was ist, wenn ich
   Firmen übernehmen will, die schon existieren aus dem NE3-Bereich für NE4?
   ... Die Funktion fehlt mir auch noch." Preise und Rechnungen bleiben in NE3. */
function Ne3UebernahmeDialog({ zu, fertig }: { zu: () => void; fertig: () => void }) {
  const [liste, setListe] = useState<any[] | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [gemacht, setGemacht] = useState<string[]>([]);
  const laden = () => api.ne4Ne3Firmen().then(setListe).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { void laden(); }, []);
  async function nehmen(x: any) {
    setLaeuft(x.id); setFehler(null);
    try {
      const r = await api.ne4FirmaAusNe3(x.id);
      setGemacht((g) => [...g, `${x.firma}: ${r.verknuepft ? 'mit der vorhandenen NE4-Firma verknüpft' : 'in NE4 angelegt, mit erstem Team'}`]);
      await laden();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(null); }
  }
  return (
    <Dialog titel="Firma aus NE3 übernehmen" breite={620} laeuft={!!laeuft} zu={gemacht.length ? fertig : zu}>
      <Fehlerkasten lage={fehler} />
      <p className="klein leise" style={{ marginTop: 0 }}>Übernommen werden Name, Kürzel, Mail, Telefon und die Anschrift als Startort, dazu das erste Team. Preise, Rechnungen und Bankdaten bleiben in NE3; die NE3-Firma ändert sich nicht.</p>
      {gemacht.map((g) => <div key={g} className="hinweis erledigt" style={{ marginBottom: 6 }}>{g}</div>)}
      {!liste ? <div className="leise klein">Lädt …</div> : (
        <table className="liste">
          <thead><tr><th>NE3-Firma</th><th>Kürzel</th><th>Ort</th><th /></tr></thead>
          <tbody>
            {liste.map((x) => (
              <tr key={x.id} style={{ cursor: 'default' }}>
                <td><b>{x.firma}</b>{x.eigenpersonal && <span className="klein leise">, eigenes Personal</span>}</td>
                <td><code>{x.kurzzeichen}</code></td>
                <td className="klein">{[x.plz, x.ort].filter(Boolean).join(' ')}</td>
                <td className="knopfleiste">
                  {x.ne4_firma_id ? <span className="klein leise">schon in NE4</span>
                    : <button type="button" className="knopf klein haupt" disabled={!!laeuft} onClick={() => void nehmen(x)}>{laeuft === x.id ? '…' : 'für NE4 übernehmen'}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="reihe" style={{ marginTop: 12 }}>
        <button type="button" className="knopf" onClick={gemacht.length ? fertig : zu}>{gemacht.length ? 'Fertig' : 'Schließen'}</button>
      </div>
    </Dialog>
  );
}

function TeamDialog({ zeile, firmen, benutzer, zu, fertig }: { zeile?: any; firmen: any[]; benutzer: any[]; zu: () => void; fertig: () => void }) {
  const [firmaId, setFirmaId] = useState(zeile?.firma_id ?? firmen[0]?.id ?? '');
  const [name, setName] = useState(zeile?.name ?? '');
  const [benutzerId, setBenutzerId] = useState(zeile?.benutzer_id ?? '');
  const [hc, setHc] = useState<boolean>(zeile ? !!zeile.darf_hc : true);
  const [gwv, setGwv] = useState<boolean>(zeile ? !!zeile.darf_gwv : true);
  const [plz, setPlz] = useState(zeile?.start_plz ?? '');
  const [ort, setOrt] = useState(zeile?.start_ort ?? '');
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set((zeile?.personen_liste ?? []).map((p: any) => p.id)));
  const [alle, setAlle] = useState<any[]>([]);
  const [neuName, setNeuName] = useState('');
  const [zugangNeu, setZugangNeu] = useState(false);
  const [zugangMail, setZugangMail] = useState('');
  const [aktiv, setAktiv] = useState<boolean>(zeile ? !!zeile.aktiv : true);
  const [meldung, setMeldung] = useState<string | null>(null);
  /* .585: GBGS-Ressource je Mitarbeiter (im Portal ist jede Person eine Ressource), ersatzweise am Team. */
  const [ressourcen, setRessourcen] = useState<{ bid: number; name: string; faehigkeit: string | null }[]>([]);
  const [teamBid, setTeamBid] = useState<string>(zeile?.gbgs_ressource_bid ? String(zeile.gbgs_ressource_bid) : '');
  useEffect(() => { api.ne4Personen().then(setAlle).catch(() => setAlle([])); api.ne4GbgsRessourcen().then(setRessourcen).catch(() => setRessourcen([])); }, []);
  const ressourceName = (bid: any) => ressourcen.find((r) => String(r.bid) === String(bid))?.name ?? (bid ? `Ressource ${bid}` : '');
  async function personRessource(id: string, wert: string) {
    try {
      await api.ne4PersonAendern(id, { gbgs_ressource_bid: wert ? Number(wert) : null });
      setAlle((a) => a.map((x) => x.id === id ? { ...x, gbgs_ressource_bid: wert ? Number(wert) : null } : x));
    } catch (f) { setFehler(ausFehler(f)); }
  }
  const personenDerFirma = alle.filter((p) => p.firma_id === firmaId);
  const umschalten = (id: string) => setGewaehlt((alt) => { const n = new Set(alt); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  /* .582: EINE PERSON ÄNDERN, AUFTEILEN ODER HERAUSNEHMEN. Marvin, 02.11.2026:
     „Ich muss die Möglichkeit haben, die Namen, die Personen zu verändern ...
     Ich habe jetzt hier zwei Personen zusammen hingeschrieben, anstatt eine." */
  const [bearbeite, setBearbeite] = useState<{ id: string; name: string } | null>(null);
  const teile = (n: string) => n.split(/\s*(?:,|;|\/|\+|&|\bund\b)\s*/).map((x) => x.replace(/\s+/g, ' ').trim()).filter((x) => x.length >= 2);
  async function personSpeichern(aufteilen: boolean) {
    if (!bearbeite) return;
    try {
      const namen = aufteilen ? teile(bearbeite.name) : [bearbeite.name.replace(/\s+/g, ' ').trim()];
      if (!namen.length) return;
      await api.ne4PersonAendern(bearbeite.id, { name: namen[0] });
      const neu: any[] = [];
      for (const n of namen.slice(1)) neu.push(await api.ne4PersonAnlegen({ firma_id: firmaId, name: n }));
      const warAn = gewaehlt.has(bearbeite.id);
      setAlle((a) => [...a.map((x) => x.id === bearbeite.id ? { ...x, name: namen[0] } : x),
                      ...neu.filter((p) => !a.some((x) => x.id === p.id)).map((p) => ({ id: p.id, firma_id: firmaId, name: p.name, team_id: null, team: null }))]
                      .sort((x, y) => x.name.localeCompare(y.name, 'de')));
      if (warAn && neu.length) setGewaehlt((g) => { const n = new Set(g); neu.forEach((p) => n.add(p.id)); return n; });
      setBearbeite(null);
    } catch (f) { setFehler(ausFehler(f)); }
  }
  async function personEntfernen() {
    if (!bearbeite) return;
    try {
      await api.ne4PersonAendern(bearbeite.id, { aktiv: false });
      setAlle((a) => a.filter((x) => x.id !== bearbeite.id));
      setGewaehlt((g) => { const n = new Set(g); n.delete(bearbeite.id); return n; });
      setBearbeite(null);
    } catch (f) { setFehler(ausFehler(f)); }
  }
  /* .582: der Ort zur Start-PLZ, wenn das Feld noch leer ist. */
  async function plzGesetzt(w: string) {
    setPlz(w);
    if (/^\d{5}$/.test(w.trim()) && !ort.trim()) {
      try { const o = await api.ne4OrtZurPlz(w.trim()); if (o.ort) setOrt((alt: string) => alt.trim() ? alt : o.ort!); } catch { /* ohne Vorschlag */ }
    }
  }
  async function personDazu() {
    const n = neuName.replace(/\s+/g, ' ').trim();
    if (!n || !firmaId) return;
    try {
      const p = await api.ne4PersonAnlegen({ firma_id: firmaId, name: n });
      setAlle((a) => a.some((x) => x.id === p.id) ? a : [...a, { id: p.id, firma_id: firmaId, name: p.name, team_id: null, team: null }].sort((x, y) => x.name.localeCompare(y.name, 'de')));
      setGewaehlt((g) => new Set(g).add(p.id));
      setNeuName('');
    } catch (f) { setFehler(ausFehler(f)); }
  }
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  async function speichern() {
    setLaeuft(true); setFehler(null);
    try {
      const ids = [...gewaehlt].filter((id) => personenDerFirma.some((p) => p.id === id));
      const k: any = { firma_id: firmaId, name, benutzer_id: zugangNeu ? null : (benutzerId || null), darf_hc: hc, darf_gwv: gwv,
                  start_plz: plz || null, start_ort: ort || null, person_ids: ids, aktiv, gbgs_ressource_bid: teamBid ? Number(teamBid) : null };
      if (zugangNeu) k.zugang_neu = { email: zugangMail.trim() };
      const e = zeile ? await api.ne4TeamAendern(zeile.id, k) : await api.ne4TeamAnlegen(k);
      if (e?.zugang) {
        const z = e.zugang.einladung ?? {};
        setMeldung(z.versendet
          ? (z.ausgang === 'umgeleitet' ? 'Team gespeichert. Der Zugang ist angelegt, die Einladung ging im Testmodus an die Testadresse.' : 'Team gespeichert. Der Zugang ist angelegt und die Einladung ist an ' + zugangMail.trim() + ' unterwegs; das Passwort setzt das Team selbst.')
          : 'Team gespeichert, Zugang angelegt. ' + (z.hinweis ?? 'Die Einladung ging nicht raus.'));
        return;
      }
      fertig();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  return (
    <Dialog titel={zeile ? 'Team ändern' : 'Neues Team'} breite={600} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      <Auswahl name="firma_id" titel="Firma" wert={firmaId} setze={setFirmaId} pflicht fehler={fehler}
               optionen={firmen.filter((f) => f.aktiv || f.id === firmaId).map((f) => ({ wert: f.id, text: `${f.name} (${f.kurz})` }))} />
      <Text name="name" titel="Team" wert={name} setze={setName} pflicht fehler={fehler} platzhalter="Team 1" breite={200} />
      {meldung && <div className="hinweis" style={{ marginBottom: 8 }}>{meldung}</div>}
      <div className="feld">
        <label>Personen</label>
        <div className="ne4-personen">
          {personenDerFirma.map((p) => {
            const an = gewaehlt.has(p.id);
            const woanders = !an && p.team_id && p.team_id !== zeile?.id ? p.team : null;
            if (bearbeite?.id === p.id) {
              const mehrere = teile(bearbeite.name).length > 1;
              return (
                <span key={p.id} className="ne4-person-bearbeiten">
                  <input value={bearbeite.name} maxLength={80} autoFocus onChange={(e) => setBearbeite({ id: p.id, name: e.target.value })}
                         onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void personSpeichern(false); } if (e.key === 'Escape') setBearbeite(null); }} />
                  <button type="button" className="knopf klein haupt" disabled={!bearbeite.name.trim()} onClick={() => void personSpeichern(false)}>Speichern</button>
                  {mehrere && <button type="button" className="knopf klein" title="Jeder Name wird eine eigene Person, beide bleiben im Team" onClick={() => void personSpeichern(true)}>In {teile(bearbeite.name).length} Personen aufteilen</button>}
                  <button type="button" className="knopf klein" title="Aus der Liste der Firma und aus dem Team nehmen" onClick={() => void personEntfernen()}>Entfernen</button>
                  <button type="button" className="knopf klein" onClick={() => setBearbeite(null)}>Abbrechen</button>
                  <select value={p.gbgs_ressource_bid ? String(p.gbgs_ressource_bid) : ''} title="Ressource dieser Person im GBGS-Portal, wird sofort gespeichert"
                          onChange={(e) => void personRessource(p.id, e.target.value)}>
                    <option value="">GBGS-Ressource: keine</option>
                    {ressourcen.map((r) => <option key={r.bid} value={String(r.bid)}>{r.name}{r.faehigkeit ? ` (${r.faehigkeit})` : ''}</option>)}
                  </select>
                </span>
              );
            }
            return (
              <span key={p.id} className="ne4-person-gruppe">
                <button type="button" className={'ne4-person-chip' + (an ? ' an' : '')} onClick={() => umschalten(p.id)}
                        title={woanders ? `steht in ${woanders}; anklicken nimmt ${p.name} dort heraus` : undefined}>
                  {an ? '✓ ' : ''}{p.name}{p.gbgs_ressource_bid ? <span className="leise" title={`GBGS: ${ressourceName(p.gbgs_ressource_bid)}`}>, GBGS</span> : null}{woanders ? <span className="leise">, {woanders}</span> : null}
                </button>
                <button type="button" className="ne4-person-stift" aria-label={`${p.name} ändern`} title="Namen ändern, aufteilen oder entfernen" onClick={() => setBearbeite({ id: p.id, name: p.name })}>✎</button>
              </span>
            );
          })}
          {!personenDerFirma.length && <span className="klein leise">Noch keine Personen bei dieser Firma.</span>}
        </div>
        <div className="ne4-person-neu">
          <input value={neuName} placeholder="Neue Person, Vor- und Nachname" maxLength={80}
                 onChange={(e) => setNeuName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void personDazu(); } }} />
          <button type="button" className="knopf" disabled={!neuName.trim() || !firmaId} onClick={() => void personDazu()}>Hinzufügen</button>
        </div>
        <div className="klein leise">In der Regel 2 Personen, je Person ein Chip. Mit ✎ änderst du den Namen, teilst zwei zusammengeschriebene Namen auf, nimmst eine Person heraus oder wählst ihre GBGS-Ressource. Der Zugang ist das Team, nicht die Person.</div>
      </div>
      <div className="feld">
        <label>GBGS-Ressource des Teams</label>
        <select value={teamBid} onChange={(e) => setTeamBid(e.target.value)}>
          <option value="">keine (die Ressourcen stehen an den Personen)</option>
          {ressourcen.map((r) => <option key={r.bid} value={String(r.bid)}>{r.name}{r.faehigkeit ? ` (${r.faehigkeit})` : ''}</option>)}
        </select>
        <div className="klein leise">Gilt nur, wenn keine Person des Teams eine eigene Ressource hat. {ressourcen.length ? '' : 'Die Liste kommt aus dem GBGS-Lesen; sie ist noch leer.'}</div>
      </div>
      <div className="feld">
        <label>Teamzugang</label>
        <select value={zugangNeu ? '__neu' : benutzerId} onChange={(e) => { const v = e.target.value; setZugangNeu(v === '__neu'); if (v !== '__neu') setBenutzerId(v); }}>
          <option value="">noch kein Zugang</option>
          <option value="__neu">neuen Zugang anlegen …</option>
          {benutzer.map((b) => <option key={b.id} value={b.id}>{b.name}{b.email ? ', ' + b.email : ''}{(b.gruppen ?? []).length ? '' : ' (ohne NE4-Gruppe)'}</option>)}
        </select>
        {zugangNeu
          ? <>
              <input type="email" value={zugangMail} placeholder="Mailadresse des Teams (Anmeldename)" style={{ marginTop: 6 }} onChange={(e) => setZugangMail(e.target.value)} />
              <div className="klein leise">Die Plattform legt den Benutzer „{(firmen.find((f) => f.id === firmaId)?.kurz ?? '')} {name || 'Team'}" in der Gruppe NE4 Teamzugang an und schickt die Einladung. Das Passwort setzt das Team selbst.</div>
            </>
          : <div className="klein leise">Ein Team hat genau einen Zugang. Vorhandene Zugänge stehen in der Liste, sonst „neuen Zugang anlegen".</div>}
      </div>
      <div className="reihe">
        <Haken name="darf_hc" titel="HC (Home Connect)" wert={hc} setze={setHc} />
        <Haken name="darf_gwv" titel="GWV (Gewerksvorbereitung)" wert={gwv} setze={setGwv} />
      </div>
      <div className="ne4-startzeile">
        <Text name="start_plz" titel="Start-PLZ" wert={plz} setze={(w) => void plzGesetzt(w)} fehler={fehler} breite={120} />
        <Text name="start_ort" titel="Startort" wert={ort} setze={setOrt} fehler={fehler} breite={260} />
      </div>
      <div className="klein leise" style={{ marginTop: -4 }}>Von wo das Team morgens losfährt. Kann leer bleiben und später unter „ändern" nachgetragen werden; zur PLZ wird der Ort vorgeschlagen.</div>
      {zeile && <Haken name="aktiv" titel="Aktiv" wert={aktiv} setze={setAktiv} />}
      <div className="reihe" style={{ marginTop: 12 }}>
        <button type="button" className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
        {meldung
          ? <button type="button" className="knopf haupt" onClick={fertig}>Fertig</button>
          : <button type="button" className="knopf haupt" onClick={speichern} disabled={laeuft || !name.trim() || !firmaId || (zugangNeu && !zugangMail.trim())}>Speichern</button>}
      </div>
    </Dialog>
  );
}

function ZugaengeDialog({ firma, benutzer, zu, fertig }: { firma: any; benutzer: any[]; zu: () => void; fertig: () => void }) {
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set((firma.zugaenge ?? []).map((z: any) => z.benutzer_id)));
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const umschalten = (id: string) => setGewaehlt((alt) => { const n = new Set(alt); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  async function speichern() {
    setLaeuft(true); setFehler(null);
    try { await api.ne4FirmaZugaenge(firma.id, [...gewaehlt]); fertig(); }
    catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  return (
    <Dialog titel={`Firmenzugänge von ${firma.name}`} breite={560} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      <p className="klein leise" style={{ marginTop: 0 }}>
        Diese Benutzer sehen die gebuchten Termine und Zahlen der Teams von {firma.name}, sonst nichts. Ein Benutzer gehört zu höchstens einer Firma.
      </p>
      {!benutzer.length && <div className="leise klein">Kein Benutzer mit einer NE4-Gruppe. Erst unter Benutzer und Rechte anlegen.</div>}
      <ul className="klein" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {benutzer.map((b) => (
          <li key={b.id} style={{ margin: '4px 0' }}>
            <label style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={gewaehlt.has(b.id)} onChange={() => umschalten(b.id)} />{' '}
              {b.name} <span className="leise">, {b.email ?? ''}, {(b.gruppen ?? []).join(', ')}</span>
            </label>
          </li>
        ))}
      </ul>
      <div className="reihe" style={{ marginTop: 12 }}>
        <button type="button" className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
        <button type="button" className="knopf haupt" onClick={speichern} disabled={laeuft}>Speichern</button>
      </div>
    </Dialog>
  );
}

/**
 * DIE KONFIGURATION VON NE4 (.544): unter Verwaltung, wenn oben NE4 gewählt ist.
 *
 * ORIGINALTEXT MARVIN (29.10.2026)
 *   „Ich würde fast sagen, dass man unter Verwaltung die Konfiguration hier
 *    nochmal aufsplittet zwischen NE3 und NE4."
 *   „In die Konfiguration müssen dann die Schätzangaben rein, die
 *    Mittelwerte, damit das dann ausgerechnet wird, wie lange brauchen die
 *    für die Mitarbeiter für ein Haus."
 * Anmeldung, zweiter Faktor, Mailversand und Telegram sind die des Hauses
 * und bleiben unter NE3.
 */
type VerwReiter = 'einstellungen' | 'gbgs' | 'mails' | 'laeufe' | 'links';
export function Ne4Verwaltung({ ich }: { ich: Anmelder }) {
  const [reiter, setReiter] = useState<VerwReiter>('einstellungen');
  const [konfig, setKonfig] = useState<any[]>([]);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const laden = () => api.ne4Konfiguration().then(setKonfig).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  const reiterListe: [VerwReiter, string][] = [['einstellungen', 'Einstellungen'], ['gbgs', 'GBGS'], ['mails', 'Mailtexte'], ['laeufe', 'Läufe']];
  if (darf(ich, 'link.pflegen')) reiterListe.push(['links', 'Externe Links']);
  return (
    <>
      <Kopf titel="NE4, Konfiguration" weg="Einstellungen, Mailtexte und Läufe des Bereichs NE4" />
      <div className="reiter" style={{ marginBottom: 12 }}>
        {reiterListe.map(([k, t]) => (
          <button key={k} type="button" aria-current={reiter === k} onClick={() => setReiter(k)}>{t}</button>
        ))}
      </div>
      <div className="inhalt">
      <Fehlerkasten lage={fehler} />
      {reiter === 'einstellungen' && <Ne4Konfiguration konfig={konfig.filter((k) => k.bereich !== 'GBGS')} ich={ich} neuLaden={laden} />}
      {/* .598: GBGS als eigener Reiter. Marvin: „die ganze Informationen zur Konfiguration unter GBGS kannst du
          verschieben unter den Reiter Konfiguration GBGS." */}
      {reiter === 'gbgs' && <Ne4Konfiguration konfig={konfig.filter((k) => k.bereich === 'GBGS')} ich={ich} neuLaden={laden} />}
      {reiter === 'mails' && <Mailtexte ich={ich} />}
      {reiter === 'laeufe' && <Laeufe />}
      {reiter === 'links' && <Links ich={ich} />}
      </div>
    </>
  );
}

/* WELCHE GRUPPE WIE HEISST UND IN WELCHER REIHE. Termine (Takt, Plätze) und
   Gebiete stammen aus den Einzelterminen und ruhen, seit NE4 ganze Häuser
   macht; die Zugangsdaten des GBGS pflegt die Seite GBGS. */
/* .598: GBGS ZUERST. Marvin, 02.11.2026: „Ich finde hier keinen Punkt, wo GBGS scharf zu stellen ist im
   NE4-Bereich. Ja, irgendwo ist er wieder versteckt." Zugang, Roboter und „scharf" stehen jetzt hier oben;
   die Karte unten auf der Seite GBGS ist weg. */
const GRUPPEN: [string, string][] = [
  ['GBGS', 'GBGS: Zugang und Roboter'],
  ['Gebäude', 'Gebäude und Schätzwerte'], ['Buchung', 'Buchung'], ['Anrufliste', 'Anrufliste'],
  ['Mails', 'Mails'], ['Kundenseite', 'Kundenseite'],
];
const SCHAETZWERTE = ['gebaeude.min_je_etage', 'gebaeude.min_je_we'];
/* .595: BEGINN VOR ENDE, auch hier. Sortiert wurde nach dem Schlüssel, und „ganztags_bis" kam vor „ganztags_von".
   Ein Paar von/bis (beginn/ende, start/stop) steht jetzt in Lesereihenfolge. */
const paarFolge = (s: string) => s.replace(/_(von|beginn|start)$/, '_0').replace(/_(bis|ende|stop)$/, '_1');

function Ne4Konfiguration({ konfig, ich, neuLaden }: { konfig: any[]; ich: Anmelder; neuLaden: () => void }) {
  const [werte, setWerte] = useState<Record<string, string>>({});
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const stellt = darf(ich, 'ne4.konfiguration.pflegen');
  useEffect(() => { setWerte(Object.fromEntries(konfig.map((k) => [k.schluessel, k.wert ?? '']))); }, [konfig]);
  async function speichern(k: any) {
    setFehler(null); setMeldung(null);
    try { await api.ne4KonfigurationSetzen(k.schluessel, werte[k.schluessel] ?? null); setMeldung(`${k.titel} gespeichert.`); neuLaden(); }
    catch (f) { setFehler(ausFehler(f)); }
  }
  async function geheimSetzen(k: any) {
    const w = await eingeben(`${k.titel} eingeben (wird verschlüsselt abgelegt, nie angezeigt):`, '', { titel: k.titel, geheim: true });
    if (w === null) return;
    setFehler(null); setMeldung(null);
    try { await api.ne4KonfigurationGeheim(k.schluessel, w || null); setMeldung(`${k.titel} ${w ? 'gesetzt' : 'gelöscht'}.`); neuLaden(); } catch (f) { setFehler(ausFehler(f)); }
  }
  const zeile = (k: any) => k.geheim ? (
    <tr key={k.schluessel} style={{ cursor: 'default' }}>
      <td><b>{k.titel}</b><div className="klein leise">{k.zweck ?? 'Wird verschlüsselt abgelegt und nie angezeigt.'}</div></td>
      <td className="klein">{k.wert === 'gesetzt' ? 'gesetzt' : 'leer'}</td>
      <td className="knopfleiste">{stellt && <button type="button" className="knopf klein" onClick={() => geheimSetzen(k)}>setzen</button>}</td>
    </tr>
  ) : k.art === 'janein' ? (
    <tr key={k.schluessel} style={{ cursor: 'default' }}>
      <td><b>{k.titel}</b><div className="klein leise">{k.zweck}</div></td>
      <td><select value={werte[k.schluessel] || 'nein'} disabled={!stellt} onChange={(e) => setWerte({ ...werte, [k.schluessel]: e.target.value })}><option value="nein">nein</option><option value="ja">ja</option></select></td>
      <td className="knopfleiste">{stellt && <button type="button" className="knopf klein" disabled={(werte[k.schluessel] || 'nein') === (k.wert || 'nein')} onClick={() => speichern(k)}>speichern</button>}</td>
    </tr>
  ) : k.schluessel === 'gebaeude.aushang_datei' ? (
    /* .495: die Aushang-Vorlage der Telekom ist eine Datei, kein Text. */
    <tr key={k.schluessel} style={{ cursor: 'default' }}>
      <td><b>Aushang-Vorlage (.docx mit Platzhaltern oder PDF)</b><div className="klein leise">Mitgeliefert ist die Telekom-Vorlage mit unserem Kopf und den Platzhaltern {'{{anschrift}} {{tage}} {{von}} {{bis}}'}; sie wird je Haus gefüllt und als PDF an die Terminbestätigung an den Eigentümer gehängt. Eine hier hochgeladene .docx ersetzt sie, eine PDF geht ungefüllt mit.</div></td>
      <td className="klein">{k.wert ? <a className="alslink" href="/api/ne4/verwaltung/aushang" target="_blank" rel="noreferrer">hochgeladen, ansehen</a> : <span>mitgelieferte Telekom-Vorlage aktiv, <a className="alslink" href="/api/ne4/verwaltung/aushang" target="_blank" rel="noreferrer">ansehen</a><div className="leise">keine eigene hochgeladen</div></span>}</td>
      <td className="knopfleiste">{stellt && <label className="knopf klein">{k.wert ? 'ersetzen' : 'hochladen'}<input type="file" accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" hidden onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ''; if (!f) return; setFehler(null); try { await api.ne4AushangHochladen(f); setMeldung('Aushang-Vorlage gespeichert.'); neuLaden(); } catch (x) { setFehler(ausFehler(x)); } }} /></label>}</td>
    </tr>
  ) : (
    <tr key={k.schluessel} style={{ cursor: 'default' }}>
      <td><b>{k.titel}</b><div className="klein leise">{k.zweck}</div></td>
      <td>{String(werte[k.schluessel] ?? '').includes('\n') || k.schluessel === 'gebaeude.fragen'
        ? <textarea value={werte[k.schluessel] ?? ''} disabled={!stellt} rows={8} style={{ width: '100%', minWidth: 260 }}
                    onChange={(e) => setWerte({ ...werte, [k.schluessel]: e.target.value })} />
        : <input value={werte[k.schluessel] ?? ''} disabled={!stellt} style={{ width: k.art === 'zahl' ? 100 : 220, maxWidth: '100%' }}
                 onChange={(e) => setWerte({ ...werte, [k.schluessel]: e.target.value })} />}</td>
      <td className="knopfleiste">
        {stellt && <button type="button" className="knopf klein" disabled={(werte[k.schluessel] ?? '') === (k.wert ?? '')} onClick={() => speichern(k)}>speichern</button>}
      </td>
    </tr>
  );
  return (
    <>
      <Fehlerkasten lage={fehler} />
      {meldung && <div className="hinweis erledigt">{meldung}</div>}
      {GRUPPEN.map(([g, titel]) => {
        const reihe = konfig.filter((k) => k.bereich === g)
          .sort((a, b) => (SCHAETZWERTE.indexOf(a.schluessel) + 1 || 99) - (SCHAETZWERTE.indexOf(b.schluessel) + 1 || 99) || paarFolge(a.schluessel).localeCompare(paarFolge(b.schluessel)));
        if (!reihe.length) return null;
        return (
          <Karte key={g} titel={titel}>
            {g === 'GBGS' && <p className="klein leise" style={{ marginTop: 0 }}>Roboter an: Termine der Gebäude gehen in seine Warteschlange statt auf die Handliste. „Scharf" nein: er macht nur einen Probelauf und schreibt an die Zeile auf der Seite GBGS, was er setzen würde (ohne Telegram). „Scharf" ja: er setzt die Termine der Connects im Bulk-Portal, immer in deutscher Zeit.</p>}
            {g === 'Gebäude' && <p className="klein leise" style={{ marginTop: 0 }}>Die Dauer eines Hauses: Etagen mal Minuten je Etage (Sammelpunkt setzen und Kabel verlegen) plus Wohneinheiten mal Minuten je Home Connect (Dose, Spleißen), geteilt durch die Arbeitszeit je Tag (Termin ganztags Beginn bis Ende) ergibt die Tage des Blocks. Eine Grundzeit gibt es nicht. Am Auftrag steht der Ist-Durchschnitt der fertigen Häuser daneben.</p>}
            <div className="rollt"><table className="liste einstellungen">
              <thead><tr><th>Einstellung</th><th>Wert</th><th /></tr></thead>
              <tbody>{reihe.map(zeile)}</tbody>
            </table></div>
          </Karte>
        );
      })}
      {konfig.some((k) => k.bereich !== 'GBGS') && <p className="klein leise">Takt, Plätze je Tag und Gebiete ruhen, seit NE4 ganze Häuser macht.</p>}
    </>
  );
}


